/**
 * <mail-it-for-you-qty>  –  Handwritten Postcards Module with Qty Dropdown
 *
 * Custom element that manages:
 *  • 3-way radio switching (printed / digital / mail-it)
 *  • Single package dropdown selector
 *  • Predefined message vs custom message type selector
 *  • Totals calculation
 *  • Validation & Add-to-Cart via Shopify AJAX API
 */

if (!customElements.get('mail-it-for-you-qty')) {
  class MailItForYouQty extends HTMLElement {
    constructor() {
      super();
      this.isActive = false;
    }

    /* ---------------------------------------------------------- */
    /*  Lifecycle                                                  */
    /* ---------------------------------------------------------- */
    connectedCallback() {
      this.container    = this.querySelector('.mailit');
      this.radioInput   = this.querySelector('.mailit__radio-input');
      this.body         = this.querySelector('.mailit__body');
      this.nameInput    = this.querySelector('[data-mailit-name]');
      this.totalCardsEl = this.querySelector('[data-mailit-total-cards]');
      this.totalPriceEl = this.querySelector('[data-mailit-total-price]');
      this.submitBtn    = this.querySelector('[data-mailit-submit]');
      this.bottomEl     = this.querySelector('[data-mailit-bottom]');

      // Dropdown & Message elements
      this.variantSelect = this.querySelector('[data-mailit-variant-select]');
      this.messageRadios = this.querySelectorAll('input[name^="mailit-msg-global-"]');
      this.textarea      = this.querySelector('[data-mailit-global-textarea]');
      this.messageOptions = this.querySelectorAll('.mailit__message-option');
      this.globalPreview  = this.querySelector('[data-mailit-global-preview]');

      this._bindEvents();
      this._handleModuleSwitch();
      this._syncVariantState();
      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Syncing variant & total states                            */
    /* ---------------------------------------------------------- */
    _syncVariantState() {
      if (!this.variantSelect) return;
      const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
      if (!selectedOption) return;

      const price = parseInt(selectedOption.dataset.price, 10) || 0;
      const cardCount = parseInt(selectedOption.dataset.cardCount, 10) || 0;
      const defaultMessage = selectedOption.dataset.defaultMessage || '';

      // Update default message preview
      if (this.globalPreview) {
        this.globalPreview.textContent = defaultMessage;
      }

      // Update totals
      if (this.totalCardsEl) this.totalCardsEl.textContent = cardCount;
      if (this.totalPriceEl) {
        const amount = price / 100;
        this.totalPriceEl.textContent =
          amount % 1 === 0
            ? '$' + amount.toLocaleString('en-US')
            : '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      // Show bottom section
      if (this.bottomEl) {
        this.bottomEl.style.display = this.isActive ? '' : 'none';
      }
    }

    /* ---------------------------------------------------------- */
    /*  Events binding                                             */
    /* ---------------------------------------------------------- */
    _bindEvents() {
      /* --- 3-way radio switching ----------------------------- */
      const radioName = this.radioInput ? this.radioInput.name : '';
      if (radioName) {
        document.querySelectorAll(`input[name="${radioName}"]`).forEach(r => {
          r.addEventListener('change', () => this._handleModuleSwitch());
        });
      }

      /* --- Variant select change ----------------------------- */
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => {
          this._syncVariantState();
          this._updateButtonState();
        });
      }

      /* --- Message type radios -------------------------------- */
      this.messageRadios.forEach(r => {
        r.addEventListener('change', () => this._handleMessageType());
      });

      /* --- Textarea live validation --------------------------- */
      if (this.textarea) {
        this.textarea.addEventListener('input', () => this._handleTextareaInput());
      }

      /* --- Name input live validation ----------------------- */
      if (this.nameInput) {
        this.nameInput.addEventListener('input', () => this._updateButtonState());
      }

      /* --- Submit -------------------------------------------- */
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', e => this._handleSubmit(e));
      }
    }

    /* ---------------------------------------------------------- */
    /*  Module switching (printed / digital / mail-it)             */
    /* ---------------------------------------------------------- */
    _handleModuleSwitch() {
      const selected = this.radioInput && this.radioInput.checked;
      this.isActive = selected;

      if (selected) {
        this.container.classList.add('mailit--active');

        // Hide competing modules
        const buyBtns     = document.querySelector('.dd-buy-buttons-wrapper');
        const ddContent   = document.querySelector('.dd-content');
        const ddMain      = document.querySelector('.digital-download-main');

        if (buyBtns)     buyBtns.style.display     = 'none';
        if (ddContent)   ddContent.classList.remove('has-checked');
        if (ddMain)      ddMain.classList.remove('has-checked');
      } else {
        this.container.classList.remove('mailit--active');
      }

      if (this.bottomEl) {
        this.bottomEl.style.display = selected ? '' : 'none';
      }
      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Message type switching                                     */
    /* ---------------------------------------------------------- */
    _handleMessageType() {
      const checked = this.querySelector('input[name^="mailit-msg-global-"]:checked');
      const value   = checked ? checked.value : 'default';

      this.messageOptions.forEach(opt => {
        const isMatch = opt.dataset.option === value;
        opt.classList.toggle('mailit__message-option--active', isMatch);
        opt.classList.remove('mailit__message-option--error');
        // Hide all error messages when switching
        const errEl = opt.querySelector('.mailit__message-error');
        if (errEl) errEl.style.display = 'none';
      });

      // When switching to custom, show error only if text already entered but < 3 chars
      if (value === 'custom' && this.textarea) {
        const text = this.textarea.value.trim();
        const customOpt = this.querySelector('.mailit__message-option[data-option="custom"]');
        const wc = text.split(/\s+/).filter(w => w.length > 0).length;
        if (text.length > 0 && wc < 2) {
          if (customOpt) customOpt.classList.add('mailit__message-option--error');
          const errorEl = customOpt && customOpt.querySelector('.mailit__message-error');
          if (errorEl) errorEl.style.display = 'block';
        }
      }

      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Textarea live validation                                   */
    /* ---------------------------------------------------------- */
    _handleTextareaInput() {
      const customOpt = this.querySelector('.mailit__message-option[data-option="custom"]');
      if (!customOpt) return;

      const text = this.textarea.value.trim();
      const errorEl = customOpt.querySelector('.mailit__message-error');

      const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;

      if (wordCount >= 2) {
        customOpt.classList.remove('mailit__message-option--error');
        customOpt.classList.add('mailit__message-option--active');
        if (errorEl) errorEl.style.display = 'none';
      } else {
        customOpt.classList.add('mailit__message-option--error');
        if (errorEl) errorEl.style.display = text.length > 0 ? 'block' : 'none';
      }

      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Button state update                                        */
    /* ---------------------------------------------------------- */
    _updateButtonState() {
      if (!this.submitBtn) return;

      if (!this.isActive) {
        this.submitBtn.disabled = true;
        return;
      }

      const checked = this.querySelector('input[name^="mailit-msg-global-"]:checked');
      const msgType = checked ? checked.value : 'default';

      let shouldDisable = false;

      // Name is always required
      if (!this.nameInput || !this.nameInput.value.trim()) {
        shouldDisable = true;
      }

      if (msgType === 'custom' && this.textarea) {
        const text = this.textarea.value.trim();
        const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
        if (wordCount < 2) {
          shouldDisable = true;
        }
      }

      this.submitBtn.disabled = shouldDisable;
    }

    /* ---------------------------------------------------------- */
    /*  Validation                                                 */
    /* ---------------------------------------------------------- */
    _validate() {
      let valid = true;

      // Name is required
      if (!this.nameInput || !this.nameInput.value.trim()) {
        valid = false;
      }

      // Textarea validation
      const checked = this.querySelector('input[name^="mailit-msg-global-"]:checked');
      const type = checked ? checked.value : 'default';
      if (type === 'custom' && this.textarea) {
        const text = this.textarea.value.trim();
        const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
        if (wordCount < 2) {
          valid = false;
        }
      }

      return valid;
    }

    _highlightErrors() {
      // Name input
      if (this.nameInput && !this.nameInput.value.trim()) {
        this.nameInput.classList.add('mailit__input--error');
        const handler = () => {
          if (this.nameInput.value.trim()) {
            this.nameInput.classList.remove('mailit__input--error');
            this.nameInput.removeEventListener('input', handler);
          }
        };
        this.nameInput.addEventListener('input', handler);
      }

      // Textarea custom message
      const checked = this.querySelector('input[name^="mailit-msg-global-"]:checked');
      const type = checked ? checked.value : 'default';
      if (type === 'custom' && this.textarea) {
        const text = this.textarea.value.trim();
        const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
        const customOpt = this.querySelector('.mailit__message-option[data-option="custom"]');
        if (wordCount < 2) {
          if (customOpt) customOpt.classList.add('mailit__message-option--error');
          const errorEl = customOpt && customOpt.querySelector('.mailit__message-error');
          if (errorEl) errorEl.style.display = 'block';
        }
      }

      // Scroll to first error
      const firstErr = this.querySelector('.mailit__input--error, .mailit__message-option--error');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ---------------------------------------------------------- */
    /*  Add to Cart                                                */
    /* ---------------------------------------------------------- */
    async _handleSubmit(e) {
      e.preventDefault();

      if (!this._validate()) {
        this._highlightErrors();
        return;
      }

      const senderName = this.nameInput.value.trim();

      const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
      const variantId = parseInt(this.variantSelect.value, 10);
      const checked = this.querySelector('input[name^="mailit-msg-global-"]:checked');
      const msgType = checked ? checked.value : 'default';
      const defaultMessage = selectedOption.dataset.defaultMessage || '';
      const message = msgType === 'custom' ? this.textarea.value.trim() : defaultMessage;

      const items = [{
        id: variantId,
        quantity: 1,
        properties: {
          '_Fulfillment': 'Handwritten Mail',
          'Signed As': senderName,
          'Message Type': msgType === 'default' ? 'Our message – written for you' : 'Your message – in your words',
          'Message': message,
        }
      }];

      // UI → loading
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('mailit__submit--loading');
      const origText = this.submitBtn.textContent;
      this.submitBtn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }

        // Fetch updated cart data for pubsub
        const cartRes = await fetch('/cart.js');
        if (!cartRes.ok) throw new Error('Could not fetch cart data');
        const cartData = await cartRes.json();

        // Publish cart update event (for cart bubbles, progress bars, etc.)
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mail-it-for-you-qty',
            cartData: cartData,
          });
        }

        // Fetch cart drawer section and update it, then open drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
          
          try {
            const sectionRes = await fetch(`${cartUrl}?section_id=cart-drawer`);
            
            if (sectionRes.ok) {
              const sectionHtml = await sectionRes.text();
              const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
              
              // Remove is-empty classes from cart drawer (if cart was empty before)
              cartDrawer.classList.remove('is-empty');
              const drawerInner = cartDrawer.querySelector('.drawer__inner');
              if (drawerInner) {
                drawerInner.classList.remove('is-empty');
              }
              
              // Remove empty state div if it exists
              const emptyState = cartDrawer.querySelector('.drawer__inner-empty');
              if (emptyState) {
                emptyState.remove();
              }
              
              // Update cart drawer items
              const drawerItems = document.querySelector('cart-drawer-items');
              const newDrawerItems = html.querySelector('cart-drawer-items');
              if (drawerItems && newDrawerItems) {
                newDrawerItems.classList.remove('is-empty');
                drawerItems.replaceWith(newDrawerItems);
              } else if (!drawerItems && newDrawerItems) {
                newDrawerItems.classList.remove('is-empty');
                const drawerInner = cartDrawer.querySelector('.drawer__inner');
                if (drawerInner) {
                  const drawerHeader = drawerInner.querySelector('.drawer__header');
                  if (drawerHeader && drawerHeader.nextElementSibling) {
                    drawerHeader.nextElementSibling.insertAdjacentElement('afterend', newDrawerItems);
                  } else {
                    drawerInner.appendChild(newDrawerItems);
                  }
                }
              }
              
              // Update cart drawer footer
              const drawerFooter = document.querySelector('.cart-drawer__footer');
              const newDrawerFooter = html.querySelector('.cart-drawer__footer');
              if (drawerFooter && newDrawerFooter) {
                drawerFooter.replaceWith(newDrawerFooter);
              } else if (!drawerFooter && newDrawerFooter) {
                const drawerFooterContainer = cartDrawer.querySelector('.drawer__footer');
                if (drawerFooterContainer) {
                  drawerFooterContainer.appendChild(newDrawerFooter);
                }
              }
              
              // Fetch and update cart icon bubble section separately
              try {
                const bubbleRes = await fetch(`${cartUrl}?section_id=cart-icon-bubble`);
                if (bubbleRes.ok) {
                  const bubbleHtml = await bubbleRes.text();
                  const cartBubble = document.getElementById('cart-icon-bubble');
                  if (cartBubble) {
                    const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
                    const bubbleSection = bubbleDoc.querySelector('#shopify-section-cart-icon-bubble, .shopify-section');
                    if (bubbleSection) {
                      cartBubble.innerHTML = bubbleSection.innerHTML;
                    }
                  }
                }
              } catch (bubbleErr) {
                console.warn('[mail-it-for-you-qty] Could not update cart icon bubble:', bubbleErr);
              }
              
              // Open the drawer after a delay
              setTimeout(() => {
                if (cartDrawer && typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                }
              }, 200);
            } else {
              window.location.href = '/cart';
            }
          } catch (err) {
            console.error('[mail-it-for-you-qty] Error updating cart drawer:', err);
            window.location.href = '/cart';
          }
        } else {
          window.location.href = '/cart';
        }

        // Reset button state
        this.submitBtn.textContent = origText;
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('mailit__submit--loading');
      } catch (err) {
        console.error('[mail-it-for-you-qty]', err);
        this.submitBtn.textContent = 'Error – Try Again';
        setTimeout(() => {
          this.submitBtn.textContent = origText;
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('mailit__submit--loading');
        }, 2500);
      }
    }
  }

  customElements.define('mail-it-for-you-qty', MailItForYouQty);
}
